import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PjudModal from './PjudModal';
import pjudLogo from './integrationsLogo/pjudLogo.png';
import './integrations.scss';
import FloatingAlert from '../../../../components/Alert/FloatingAlert';
import {
  createPjudIntegration,
  deactivatePjudIntegrationByUserId,
  getPjudIntegrationByUserId,
} from '../../../../services/Configuration/IntegrationService';
import { getCurrentUser } from '../../../../services/authService';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { integrationsI18n } from './i18n';

const Integrations = () => {
  const { language } = useLanguage();
  const integrationsLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: integrationsI18n });
  const { DesignSystemComponents } = useOutletContext() ?? {};
  const { Card, Button, Badge, Modal, Input } = DesignSystemComponents ?? {};

  const [showPjudModal, setShowPjudModal] = useState(false);
  const [pjudIntegration, setPjudIntegration] = useState(null);
  const [loadingPjud, setLoadingPjud] = useState(false);
  const [savingPjud, setSavingPjud] = useState(false);
  const [statusAlert, setStatusAlert] = useState(null);

  useEffect(() => {
    integrationsI18n.changeLanguage(integrationsLanguage);
  }, [integrationsLanguage]);

  const integrations = [
    {
      id: 'pjud',
      name: t('integrations_pjud_name'),
      description: t('integrations_pjud_description'),
    },
  ];

  const statusStyles = {
    active: { label: t('integrations_status_active'), color: 'success' },
    disabled: { label: t('integrations_status_inactive'), color: 'error' },
  };

  useEffect(() => {
    const user = getCurrentUser();
    if (!user?.userId) return;
    let mounted = true;
    const fetchPjud = async () => {
      try {
        setLoadingPjud(true);
        const data = await getPjudIntegrationByUserId(user.userId);
        if (mounted) {
          setPjudIntegration(data);
        }
      } catch (error) {
        console.error('Error fetching PJUD integration:', error);
      } finally {
        if (mounted) {
          setLoadingPjud(false);
        }
      }
    };
    fetchPjud();
    return () => {
      mounted = false;
    };
  }, []);

  const onConfigure = (integration) => {
    if (integration.id === 'pjud') {
      setShowPjudModal(true);
      return;
    }
  };

  const onClosePjudModal = () => setShowPjudModal(false);
  const onSavePjud = async (form) => {
    if (!form?.username?.trim() || !form?.password?.trim()) return;

    const user = getCurrentUser();
    if (!user?.userId) return;

    try {
      setSavingPjud(true);
      await createPjudIntegration({
        account: form.username.trim(),
        password: form.password.trim(),
        testConnection: false,
        active: true,
        userId: user.userId,
      });
      const data = await getPjudIntegrationByUserId(user.userId);
      setPjudIntegration(data);
      setShowPjudModal(false);
      setStatusAlert({
        type: 'success',
        message: t('integrations_alert_save_success'),
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t('integrations_alert_save_error');
      setStatusAlert({ type: 'danger', message });
      console.error('Error saving PJUD integration:', error);
    } finally {
      setSavingPjud(false);
    }
  };
  const onUnlinkPjud = async () => {
    const user = getCurrentUser();
    if (!user?.userId) return;

    try {
      setSavingPjud(true);
      await deactivatePjudIntegrationByUserId(user.userId);
      const data = await getPjudIntegrationByUserId(user.userId);
      setPjudIntegration(data);
      setShowPjudModal(false);
      setStatusAlert({
        type: 'success',
        message: t('integrations_alert_unlink_success'),
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t('integrations_alert_unlink_error');
      setStatusAlert({ type: 'danger', message });
      console.error('Error unlinking PJUD integration:', error);
    } finally {
      setSavingPjud(false);
    }
  };

  if (!Card || !Button || !Badge) return null;

  return (
    <Card specialPadding='p-4' className='integrations-page'>
      {statusAlert?.message && (
        <FloatingAlert
          message={statusAlert.message}
          type={statusAlert.type}
          onClose={() => setStatusAlert(null)}
        />
      )}
      <header className='integrations-page__header mb-4'>
        <div>
          <h2 className='mb-1'>{t('integrations_title')}</h2>
          <p className='caption font-light mb-0'>
            {t('integrations_subtitle')}
          </p>
        </div>
      </header>

      <div className='integrations-grid'>
        {integrations.map((integration) => {
          const isActive = pjudIntegration?.active === true;
          const status = isActive ? statusStyles.active : statusStyles.disabled;

          return (
            <Card
              key={integration.id}
              specialPadding='p-4'
              className='integration-card'
            >
              <div className='integration-card-body d-flex flex-column h-100'>
                <div className='integration-card__header mb-2'>
                  <div className='d-flex gap-2 align-items-center'>
                    <img
                      className='integration-card__logo'
                      src={pjudLogo}
                      alt={t('integrations_modal_logo_alt')}
                    />
                    <h3 className='integration-card__title mb-0'>
                      {integration.name}
                    </h3>
                  </div>

                  {loadingPjud ? (
                    <span
                      className='spinner-border spinner-border-sm text-primary'
                      role='status'
                      aria-label={t('integrations_loading_aria')}
                    />
                  ) : (
                    <Badge
                      text={status.label}
                      color={status.color}
                      size='sm'
                      autoWidth
                    />
                  )}
                </div>
                <p className='integration-card__description mb-4'>
                  {integration.description}
                </p>
                <div className='integration-card__action'>
                  <Button
                    variant='naked'
                    size='sm'
                    label={t('integrations_configure_button')}
                    onClick={() => onConfigure(integration)}
                    disabled={loadingPjud}
                  />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <PjudModal
        Modal={Modal}
        Input={Input}
        show={showPjudModal}
        onClose={onClosePjudModal}
        onSave={onSavePjud}
        onUnlink={onUnlinkPjud}
        initialData={pjudIntegration}
        isSaving={savingPjud}
      />
    </Card>
  );
};

export default Integrations;
